// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 32-knowledge.md
import * as React from 'react';
import { toast } from 'sonner';

import { ClaudeMark } from '@/components/knowledge/ClaudeMark';
import { ScopeTile } from '@/components/knowledge/ScopeTile';
import { useScopeResolvers } from '@/components/knowledge/useScopeResolvers';
import { canMutateScope, formatKnowledgeDate } from '@/components/knowledge/util';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { api } from '@/lib/api';
import { GLDrawer } from '@/components/greenlight/GLDrawer';
import type { KnowledgeProposal } from '@greenlight/contract';

// ---------------------------------------------------------------------------
// DiffBody helper — line-membership highlighter for the proposed pane
// ---------------------------------------------------------------------------

function DiffBody({
  live,
  proposed,
  isNew,
}: {
  live: string;
  proposed: string;
  isNew: boolean;
}): React.JSX.Element {
  const liveLines = new Set(live.split('\n'));
  const lines = proposed.split('\n');

  // Line-membership heuristic (not a true LCS diff): a line is "added" if it
  // isn't present anywhere in the live body. Blank lines almost always exist in
  // the live body, so a blank inside a new block would break the highlight bar;
  // a blank therefore inherits the previous line's added state to keep a
  // contiguous added block visually whole.
  const addedFlags: boolean[] = [];
  for (const [i, line] of lines.entries()) {
    if (isNew) {
      addedFlags.push(true);
    } else if (line.trim() === '') {
      addedFlags.push(addedFlags[i - 1] ?? false);
    } else {
      addedFlags.push(!liveLines.has(line));
    }
  }

  return (
    <pre className="whitespace-pre-wrap break-words rounded-gl border border-border bg-card p-0 font-mono text-xs leading-relaxed overflow-hidden">
      {lines.map((line, i) => {
        const added = addedFlags[i] ?? false;
        return (
          <div
            key={i}
            className={
              added
                ? 'bg-[hsl(var(--status-green)/0.13)] border-l-2 border-[hsl(var(--status-green))] pr-3'
                : 'pr-3 border-l-2 border-transparent'
            }
            data-testid={added ? 'proposal-diff-added' : undefined}
          >
            <span
              aria-hidden="true"
              className={
                added
                  ? 'select-none pl-1.5 pr-1.5 font-semibold text-[hsl(var(--status-green))]'
                  : 'select-none pl-1.5 pr-1.5 text-transparent'
              }
            >
              +
            </span>
            {line === '' ? ' ' : line}
          </div>
        );
      })}
    </pre>
  );
}

// ---------------------------------------------------------------------------
// ProposalReview
// ---------------------------------------------------------------------------

export interface ProposalReviewProps {
  proposal: KnowledgeProposal | null;
  userRole: string | undefined;
  onClose: () => void;
  onReviewed: (affectedEntryId: string | null) => void;
}

export function ProposalReview({
  proposal,
  userRole,
  onClose,
  onReviewed,
}: ProposalReviewProps): React.JSX.Element {
  const lastProposalRef = React.useRef<KnowledgeProposal | null>(proposal);
  if (proposal) lastProposalRef.current = proposal;
  const view = proposal ?? lastProposalRef.current;

  const resolve = useScopeResolvers();
  const display = view
    ? resolve({ scope: view.scope, integrationId: view.integrationId, appId: view.appId })
    : null;

  const liveQueryKey = ['knowledge-entry', view?.entryId ?? null];
  const { data: liveData, isPending: liveIsPending } = api.knowledge.getEntry.useQuery(
    liveQueryKey,
    { params: { id: view?.entryId ?? '' } },
    { queryKey: liveQueryKey, enabled: proposal?.entryId != null },
  );
  const acceptMutation = api.knowledge.acceptProposal.useMutation();
  const rejectMutation = api.knowledge.rejectProposal.useMutation();

  const liveEntry = liveData?.status === 200 ? liveData.body : null;
  const [editedBody, setEditedBody] = React.useState('');
  const [editing, setEditing] = React.useState(false);
  const [rejectingNotes, setRejectingNotes] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (proposal) {
      setEditedBody(proposal.bodyMd);
      setEditing(false);
      setRejectingNotes(null);
    }
  }, [proposal?.id, proposal]);

  const canReview = view ? canMutateScope(userRole, view.scope) : false;

  async function onAccept(withEdits: boolean): Promise<void> {
    if (!proposal) return;
    const result = await acceptMutation.mutateAsync({
      params: { id: proposal.id },
      body: withEdits ? { bodyMd: editedBody } : {},
    });
    if (result.status === 200) {
      toast.success(
        result.body.lastEditorKind === 'human'
          ? 'Accepted with edits — entry updated.'
          : 'Accepted — entry updated.',
      );
      onReviewed(result.body.id);
    } else {
      toast.error('Accept failed.');
    }
  }

  async function onReject(): Promise<void> {
    if (!proposal || !rejectingNotes) return;
    const result = await rejectMutation.mutateAsync({
      params: { id: proposal.id },
      body: { reviewNotes: rejectingNotes },
    });
    if (result.status === 200) {
      toast.success('Proposal rejected.');
      onReviewed(null);
    } else {
      toast.error('Reject failed.');
    }
  }

  const titleNode = view ? (
    <div className="flex items-start gap-3">
      {display && <ScopeTile {...display.tile} size={44} />}
      <div className="min-w-0 flex-1">
        <span className="block font-mono text-[10.5px] font-medium uppercase tracking-[0.06em] text-muted-foreground">
          {view.entryId ? 'Proposed edit' : 'Proposed new entry'}
          {display ? ` · ${display.scopeLabel}` : ''}
        </span>
        <p className="text-[19px] font-bold tracking-tight leading-tight">{view.title}</p>
        <p className="mt-0.5 font-mono text-[11px] font-normal text-muted-foreground">
          {view.topic} · proposed {formatKnowledgeDate(view.proposedAt)}
        </p>
      </div>
    </div>
  ) : (
    'Proposal review'
  );

  let footerNode: React.ReactNode;
  if (view) {
    if (rejectingNotes !== null) {
      footerNode = (
        <div className="space-y-2">
          <label className="text-xs font-medium" htmlFor="reject-notes">
            Review notes (required)
          </label>
          <Textarea
            id="reject-notes"
            value={rejectingNotes}
            onChange={(e) => setRejectingNotes(e.target.value)}
            rows={3}
            data-testid="reject-notes-input"
          />
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setRejectingNotes(null)}>
              Back
            </Button>
            <Button
              variant="destructive"
              size="sm"
              disabled={!rejectingNotes.trim() || rejectMutation.isPending}
              onClick={onReject}
              data-testid="proposal-confirm-reject"
            >
              Confirm reject
            </Button>
          </div>
        </div>
      );
    } else if (canReview) {
      footerNode = (
        <div className="flex flex-col-reverse gap-2 sm:flex-wrap sm:flex-row sm:items-center sm:justify-end">
          {editing ? (
            <>
              <Button
                variant="outline"
                data-testid="proposal-edit-cancel"
                className="h-12 w-full sm:h-auto sm:w-auto"
                onClick={() => {
                  setEditing(false);
                  setEditedBody(view.bodyMd);
                }}
              >
                Cancel
              </Button>
              <Button
                data-testid="proposal-accept-edits"
                disabled={acceptMutation.isPending}
                className="h-12 w-full sm:h-auto sm:w-auto"
                onClick={() => onAccept(true)}
              >
                Save &amp; accept
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="ghost"
                data-testid="proposal-close"
                className="h-12 w-full bg-muted/50 sm:h-auto sm:w-auto sm:bg-transparent"
                onClick={onClose}
              >
                Close
              </Button>
              <Button
                variant="destructive-outline"
                data-testid="proposal-reject"
                className="h-12 w-full sm:h-auto sm:w-auto"
                onClick={() => setRejectingNotes('')}
              >
                Reject
              </Button>
              <Button
                variant="outline"
                data-testid="proposal-edit"
                className="h-12 w-full sm:h-auto sm:w-auto"
                onClick={() => setEditing(true)}
              >
                Edit
              </Button>
              <Button
                data-testid="proposal-accept"
                disabled={acceptMutation.isPending}
                className="h-12 w-full sm:h-auto sm:w-auto"
                onClick={() => onAccept(false)}
              >
                Accept
              </Button>
            </>
          )}
        </div>
      );
    }
  }

  return (
    <GLDrawer
      open={proposal != null}
      onOpenChange={(open) => !open && onClose()}
      variant="panel"
      mobileDragResetKey={proposal?.id}
      defaultWidth={720}
      minWidth={360}
      storageKey="gl-proposal-review-panel-width"
      title={titleNode}
      srDescription={view?.title ?? 'Proposal review'}
      mobileClassName="max-h-[92dvh] rounded-t-[20px]"
      contentProps={{ 'data-testid': 'proposal-review' }}
      footer={footerNode}
    >
      {view && (
        <>
          <div className="rounded-gl border border-border bg-muted/35 p-3">
            <div className="flex items-center gap-1.5 mb-1">
              <ClaudeMark size={13} />
              <span className="text-[11px] font-medium text-foreground">Agent rationale</span>
            </div>
            <p className="text-[12px] text-muted-foreground leading-relaxed">{view.rationale}</p>
          </div>

          {!canReview && (
            <Alert data-testid="proposal-readonly" className="mt-4">
              <AlertTitle>Read-only</AlertTitle>
              <AlertDescription>
                {view.scope === 'app'
                  ? 'Reviewing app-scope proposals requires the app owner, an editor, or an IT role.'
                  : `Reviewing ${view.scope}-scope proposals requires the org_admin role.`}
              </AlertDescription>
            </Alert>
          )}

          <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2" data-testid="proposal-diff">
            {/* Live pane */}
            <div className="space-y-1">
              <p className="text-[11px] font-medium text-muted-foreground">
                {view.entryId ? `Live · v${liveEntry?.version ?? '?'}` : 'Live'}
              </p>
              <pre className="whitespace-pre-wrap break-words rounded-gl border border-border bg-muted/20 p-3 font-mono text-xs leading-relaxed">
                {view.entryId ? (
                  liveEntry?.bodyMd != null && liveEntry.bodyMd !== '' ? (
                    liveEntry.bodyMd
                  ) : liveEntry == null ? (
                    <span className="text-muted-foreground">Loading live entry…</span>
                  ) : (
                    liveEntry.bodyMd
                  )
                ) : (
                  <span className="text-muted-foreground">No live entry yet — new topic.</span>
                )}
              </pre>
            </div>

            {/* Proposed pane */}
            <div className="space-y-1">
              <p
                id="proposal-proposed-label"
                className="text-[11px] font-medium text-muted-foreground"
              >
                {editing ? 'Your edited proposal' : 'Proposed'}
              </p>
              {editing ? (
                <Textarea
                  value={editedBody}
                  onChange={(e) => setEditedBody(e.target.value)}
                  rows={16}
                  className="font-mono text-xs"
                  aria-labelledby="proposal-proposed-label"
                />
              ) : view.entryId != null && liveIsPending ? (
                // Hold off rendering the diff only while the live fetch is
                // in flight — prevents every line flashing as "added" against
                // an empty live string. Once the query settles (success or
                // error) we render DiffBody; a failed fetch falls back to
                // live="" which highlights everything, keeping the proposal
                // visible rather than hiding it behind a spinner forever.
                <pre className="whitespace-pre-wrap break-words rounded-gl border border-border bg-card p-3 font-mono text-xs leading-relaxed">
                  <span className="text-muted-foreground">Loading diff…</span>
                </pre>
              ) : (
                <DiffBody
                  live={liveEntry?.bodyMd ?? ''}
                  proposed={view.bodyMd}
                  isNew={view.entryId == null}
                />
              )}
            </div>
          </div>
        </>
      )}
    </GLDrawer>
  );
}
